import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

import Avatar from "../avatar/Avatar";
import ToolTip from "../tooltip/ToolTip";
import type { FollowUser } from "../../app/services/follows";
import { getUserDisplayName } from "../../utils/user/displayName";

/**
 * リスト形式のユーザー行（C 形式：アバター + 表示名 + 補助テキスト + 右アクション）。
 *
 * 役割の整理（UI ガイドの 3 形式）:
 *  - A: AvatarStack       … アイコンのみ並列、選択 UI 向け（`avatar-stack/`）
 *  - B: カード             … RecipientStep の縦長カード（選択 UI 向け、ローカル実装）
 *  - C: UserListRow       … 本コンポーネント。ユーザーへの操作 / 一覧 / 並び替え
 *
 * variant:
 *  - `"bordered"`（既定）: 旧フォロー画面と同じ border + shadow + hover bg
 *  - `"flat"`             : プロジェクトメンバー一覧と同じ flat レイアウト
 *
 * Slot API（任意）:
 *  - `leadingSlot`  Avatar の左に置く要素（ドラッグハンドル / 状態ラベルなど）
 *  - `avatarSlot`   Avatar を完全に差し替えたい場合の override
 *  - `metaSlot`     表示名・補助テキスト列の末尾に追加する小要素（"（責任者）" 等）
 *  - `trailingSlot` 右側に置く任意の要素（任意のフォロー/メニュー/招待ボタン群）
 *
 * 後方互換:
 *  - `trailingSlot` 未指定時のみ legacy `rightAction` API が有効になる。
 *  - フォロー / フォロワー画面の挙動は変更なし。
 */
const Row = styled.div.attrs({
  className: "u-row-between u-gap16",
})<{ $variant: "bordered" | "flat" }>`
  width: 100%;
  padding: 8px 8px;
  border-radius: 4px;
  background-color: #fff;

  ${({ $variant }) =>
    $variant === "bordered"
      ? css`
          border: 1px solid var(--soremo-border);
          box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
          &:hover {
            background-color: var(--soremo-background);
          }
        `
      : css`
          border: 1px solid transparent;
          box-shadow: none;
          &:hover {
            background-color: var(--soremo-background);
          }
        `}
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 24px;
    color: var(--soremo-light-gray);
    transition:
      color 0.2s ease,
      scale 0.2s ease;
  }

  &:hover .material-symbols-rounded {
    color: var(--soremo-blue);
    scale: 1.1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FollowingActionButton = styled(ActionButton)<{ $isFollowing: boolean }>`
  .material-symbols-rounded {
    color: ${(props) =>
      props.$isFollowing ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
    font-variation-settings: ${(props) =>
      props.$isFollowing ? "'FILL' 1" : "'FILL' 0"};
  }
`;

// ブロック中（＝削除済み）であることを示すラベルボタン。
// クリックでブロック解除して通常のフォロワー行に戻す。
const BlockedLabelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--soremo-border);
  background: var(--soremo-background);
  color: var(--soremo-light-gray);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #f0f0f0;
    color: var(--soremo-blue);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .material-symbols-rounded {
    font-size: 16px;
  }
`;

/**
 * 表示に必要な最小フィールド。`FollowUser` / `ProjectMembership` どちらも受けられる
 * 緩いインターフェース。Optional フィールドが欠けた場合は内部で補完する。
 */
export interface UserListRowUser {
  id?: number | string;
  avatar_url?: string | null;
  display_name?: string | null;
  display_name_jp?: string | null;
  display_name_en?: string | null;
  title?: string | null;
  title_jp?: string | null;
  title_en?: string | null;
  affiliation?: string | null;
  affiliation_jp?: string | null;
  affiliation_en?: string | null;
  is_following?: boolean;
}

export type UserListRowVariant = "bordered" | "flat";
export type UserListRowRightAction =
  | "delete"
  | "follow-toggle"
  | "delete-blocked"
  | "none";

interface UserListRowProps {
  user: UserListRowUser | FollowUser;
  /** 行の見た目。既定 "bordered"。 */
  variant?: UserListRowVariant;
  /** Avatar 表示サイズ（px）。既定 40。 */
  avatarSize?: number;
  /** Avatar にオンラインバッジを出すか。 */
  isOnline?: boolean;

  /** Avatar の左に挿入する任意要素（ドラッグハンドル / 状態ラベル等）。 */
  leadingSlot?: ReactNode;
  /** Avatar 自体を完全に差し替えたい場合の override。 */
  avatarSlot?: ReactNode;
  /** 補助テキスト列の末尾に追加表示する小要素（"（責任者）" 等）。 */
  metaSlot?: ReactNode;
  /** 右側に表示する任意要素。指定時は legacy `rightAction` を無視する。 */
  trailingSlot?: ReactNode;

  // ===== legacy API（trailingSlot 未指定時のみ有効）=====
  rightAction?: UserListRowRightAction;
  isFollowing?: boolean;
  isActionDisabled?: boolean;
  onClickDelete?: (_target: FollowUser) => void;
  onToggleFollow?: (_target: FollowUser) => void;
  onClickUnblock?: (_target: FollowUser) => void;

  className?: string;
}

const resolveDisplayName = (user: UserListRowUser): string => {
  if (user.display_name) return user.display_name;
  // FollowUser 互換: getUserDisplayName が jp/en を解決する。
  return getUserDisplayName(user as unknown as FollowUser) || "";
};

const resolveTitle = (user: UserListRowUser): string =>
  user.title ?? user.title_jp ?? user.title_en ?? "";

const resolveAffiliation = (user: UserListRowUser): string =>
  user.affiliation ?? user.affiliation_jp ?? user.affiliation_en ?? "";

const UserListRow: React.FC<UserListRowProps> = ({
  user,
  variant = "bordered",
  avatarSize = 40,
  isOnline = false,
  leadingSlot,
  avatarSlot,
  metaSlot,
  trailingSlot,
  rightAction = "none",
  isFollowing = false,
  isActionDisabled = false,
  onClickDelete,
  onToggleFollow,
  onClickUnblock,
  className,
}) => {
  const u = user as UserListRowUser;
  const displayName = resolveDisplayName(u);
  const titleText = resolveTitle(u);
  const affiliationText = resolveAffiliation(u);

  const renderLegacyRightAction = () => {
    if (trailingSlot !== undefined) return trailingSlot;
    if (rightAction === "delete") {
      return (
        <ActionButton
          type="button"
          onClick={() => onClickDelete?.(user as FollowUser)}
          disabled={isActionDisabled}
          aria-label="フォロワーを削除"
        >
          <span className="material-symbols-rounded">delete</span>
        </ActionButton>
      );
    }
    if (rightAction === "delete-blocked") {
      return (
        <ToolTip description="クリックで削除を解除">
          <BlockedLabelButton
            type="button"
            onClick={() => onClickUnblock?.(user as FollowUser)}
            disabled={isActionDisabled}
            aria-label="削除を解除"
          >
            <span className="material-symbols-rounded">undo</span>
            <span className="bodytext-11">削除中</span>
          </BlockedLabelButton>
        </ToolTip>
      );
    }
    if (rightAction === "follow-toggle") {
      return (
        <FollowingActionButton
          type="button"
          $isFollowing={isFollowing}
          onClick={() => onToggleFollow?.(user as FollowUser)}
          disabled={isActionDisabled}
          aria-label={isFollowing ? "フォローを解除" : "フォローする"}
        >
          <span className="material-symbols-rounded">bookmark</span>
        </FollowingActionButton>
      );
    }
    return null;
  };

  return (
    <Row $variant={variant} className={className}>
      <div className="u-row u-gap8 u-align-center">
        {leadingSlot}
        {avatarSlot ?? (
          <Avatar
            size={avatarSize}
            src={u.avatar_url ?? undefined}
            name={displayName}
            isOnline={isOnline}
          />
        )}
        <ul className="u-col u-gap4">
          <li className="heading-spacing-13 u-line-height-100">
            {displayName}
          </li>
          {titleText && (
            <li className="bodytext-11 u-line-height-100 u-text-light-gray">
              {titleText}
            </li>
          )}
          {affiliationText && (
            <li className="bodytext-11 u-line-height-100 u-text-light-gray">
              {affiliationText}
            </li>
          )}
          {metaSlot}
        </ul>
      </div>

      {renderLegacyRightAction()}
    </Row>
  );
};

export default UserListRow;
